/**
 * FX 模拟行情生成工具
 *
 * <p>基于后端确定性种子，在本地用几何布朗运动生成逐 tick 价格，
 * tick 与真实时间绑定（每 tickIntervalMs 毫秒一个 tick），
 * 同一日期任意设备同 tick 序号价格一致。</p>
 */
import { createGenerator, nextNormal } from './prng'

const DAY_MS = 24 * 60 * 60 * 1000
const DAY_JITTER = 0.004

/**
 * 行情序列缓存：key = 日期 + ":" + 品种代码
 */
const seriesCache = new Map()
let lastDayKey = ''
let clockOffsetMs = 0

/**
 * 设置设备时钟与服务器时间的偏差
 *
 * <p>行情 tick 以服务器时间为基准，避免设备时钟不准导致不同设备落在不同 tick。</p>
 *
 * @param {number} offsetMs 服务器时间 - 本地时间的毫秒差
 */
export function setClockOffset(offsetMs) {
	clockOffsetMs = Number.isFinite(offsetMs) ? offsetMs : 0
}

/**
 * 获取校正后的当前时间戳
 *
 * @returns {number} 毫秒时间戳
 */
function now() {
	return Date.now() + clockOffsetMs
}

/**
 * 获取北京时间当日日期键（yyyyMMdd）
 *
 * @returns {string} 日期键
 */
export function getDayKey() {
	const current = new Date(now())
	const utc8 = new Date(current.getTime() + 8 * 3600 * 1000)
	const y = utc8.getUTCFullYear()
	const m = String(utc8.getUTCMonth() + 1).padStart(2, '0')
	const d = String(utc8.getUTCDate()).padStart(2, '0')
	return `${y}${m}${d}`
}

/**
 * 获取北京时间当日零点时间戳
 *
 * @returns {number} 当日零点毫秒时间戳
 */
export function dayStartUtc8() {
	const utc8 = new Date(now() + 8 * 3600 * 1000)
	const y = utc8.getUTCFullYear()
	const m = utc8.getUTCMonth()
	const d = utc8.getUTCDate()
	return Date.UTC(y, m, d) - 8 * 3600 * 1000
}

/**
 * 计算当前 tick 序号
 *
 * @param {number} tickIntervalMs tick 间隔（毫秒）
 * @returns {number} 从当日零点开始的 tick 序号
 */
export function currentTickIndex(tickIntervalMs) {
	return Math.floor((now() - dayStartUtc8()) / tickIntervalMs)
}

/**
 * 清空价格序列缓存
 *
 * <p>切换交易日种子时需要清空，避免旧种子生成的序列被继续追加。</p>
 */
export function resetSeriesCache() {
	seriesCache.clear()
	lastDayKey = ''
}

/**
 * 获取品种价格序列（含当日开盘价，prices[i] 为第 i 个 tick 的价格）
 *
 * <p>内部按日期+品种缓存，跨日自动清理；增量续算避免每次全量递推。</p>
 *
 * @param {object} symbol 品种配置
 * @param {number} seed 当日种子
 * @param {number} tickIndex 目标 tick 序号
 * @returns {number[]} 价格序列
 */
export function getPriceSeries(symbol, seed, tickIndex) {
	const day = getDayKey()
	if (lastDayKey !== day) {
		lastDayKey = day
		seriesCache.clear()
	}
	const key = day + ':' + symbol.code
	let entry = seriesCache.get(key)
	if (!entry) {
		entry = generateFromScratch(symbol, seed, tickIndex)
		seriesCache.set(key, entry)
	} else if (tickIndex > entry.tickIndex) {
		appendTicks(entry, symbol, seed, tickIndex)
	}
	return entry.prices
}

/**
 * 获取最新价格
 *
 * @param {object} symbol 品种配置
 * @param {number} seed 当日种子
 * @param {number} tickIndex 当前 tick 序号
 * @returns {number} 最新价格
 */
export function getLatestPrice(symbol, seed, tickIndex) {
	const prices = getPriceSeries(symbol, seed, tickIndex)
	return prices[prices.length - 1]
}

/**
 * 获取最近 K 线（已完成的整根）
 *
 * @param {object} symbol 品种配置
 * @param {number} seed 当日种子
 * @param {number} tickIndex 当前 tick 序号
 * @param {number} ticksPerBar 每根 K 线的 tick 数
 * @param {number} barCount 返回的 K 线数量
 * @returns {Array<{open: number, high: number, low: number, close: number}>} K 线数组
 */
export function getKlines(symbol, seed, tickIndex, ticksPerBar, barCount) {
	const prices = getPriceSeries(symbol, seed, tickIndex)
	const bars = aggregateKlines(prices, ticksPerBar)
	// 追加当前尚未收线的 K 线，使图表每个 tick 都有可见变化
	const remainder = (prices.length - 1) % ticksPerBar
	if (remainder > 0) {
		const start = prices.length - remainder
		let high = prices[start]
		let low = prices[start]
		for (let i = start + 1; i < prices.length; i++) {
			if (prices[i] > high) high = prices[i]
			if (prices[i] < low) low = prices[i]
		}
		bars.push({
			open: prices[start],
			high,
			low,
			close: prices[prices.length - 1],
			forming: true
		})
	}
	return bars.slice(-barCount)
}

/**
 * 将价格序列聚合成 K 线
 *
 * @param {number[]} prices 价格序列（含开盘价）
 * @param {number} ticksPerBar 每根 K 线的 tick 数
 * @returns {Array<{open: number, high: number, low: number, close: number}>} K 线数组
 */
export function aggregateKlines(prices, ticksPerBar) {
	const bars = []
	const barCount = Math.floor((prices.length - 1) / ticksPerBar)
	for (let i = 0; i < barCount; i++) {
		const start = i * ticksPerBar
		const end = start + ticksPerBar
		let high = prices[start]
		let low = prices[start]
		for (let j = start + 1; j < end; j++) {
			if (prices[j] > high) high = prices[j]
			if (prices[j] < low) low = prices[j]
		}
		bars.push({
			open: prices[start],
			high,
			low,
			close: prices[end - 1]
		})
	}
	return bars
}

/**
 * 从零开始生成序列
 *
 * @param {object} symbol 品种配置
 * @param {number} seed 当日种子
 * @param {number} tickIndex 目标 tick 序号
 * @returns {{prices: number[], tickIndex: number, randState: number}} 序列缓存条目
 */
function generateFromScratch(symbol, seed, tickIndex) {
	const gen = createGenerator(seed >>> 0)
	const open = roundPrice(symbol.basePrice * (1 + (gen.next() - 0.5) * DAY_JITTER))
	const prices = [open]
	let price = open
	const sigma = symbol.sigma
	const drift = -(sigma * sigma) / 2
	for (let i = 0; i < tickIndex; i++) {
		price = price * Math.exp(drift + sigma * nextNormal(gen))
		prices.push(price)
	}
	return { prices, tickIndex, randState: gen.getState() }
}

/**
 * 从缓存检查点增量续算序列
 *
 * @param {{prices: number[], tickIndex: number, randState: number}} entry 缓存条目
 * @param {object} symbol 品种配置
 * @param {number} seed 当日种子
 * @param {number} tickIndex 目标 tick 序号
 */
function appendTicks(entry, symbol, seed, tickIndex) {
	const gen = createGenerator(seed >>> 0)
	gen.setState(entry.randState)
	let price = entry.prices[entry.prices.length - 1]
	const sigma = symbol.sigma
	const drift = -(sigma * sigma) / 2
	for (let i = entry.tickIndex; i < tickIndex; i++) {
		price = price * Math.exp(drift + sigma * nextNormal(gen))
		entry.prices.push(price)
	}
	entry.tickIndex = tickIndex
	entry.randState = gen.getState()
}

/**
 * 价格四舍五入到 6 位小数
 *
 * @param {number} price 价格
 * @returns {number} 四舍五入后的价格
 */
function roundPrice(price) {
	return Math.round(price * 1000000) / 1000000
}
